/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { useDelayedHover } from "hooks/discovery";
import { checkedTypes } from "constants/types";
import parse from "html-react-parser";
import { addChecked } from "slices/discovery/actions/theme";
import {
  setByContentHoveredTerm,
  uncheckAndCollapse,
} from "slices/discovery/discoverySlice";
import Checkbox from "@visiblethread/react-components/dist/components/Checkbox";
import Gradient from "components/Gradient";
import ActionButtons from "layout/discovery/ActionButtons";
import { PLACEMENTS } from "constants/discovery";
import Ellipsis from "components/Ellipsis";
import { itemProps } from "./constants";
import { compareStringsCaseInsensitive } from "utils";

const Item = ({ item, checked, maxOccurrences }) => {
  const dispatch = useDispatch();
  const itemRef = useRef();

  const checkboxRef = useRef();

  const themes = useSelector((state) => state.discovery.themes);
  const checkedItems = themes?.filter(
    (theme) => theme.checked && theme.NounPhrase !== undefined
  );
  const hasOccurrences = item.Occurrences !== undefined && item.Occurrences > 0;
  const documentView =
    useSelector((state) => state.discovery.config.documentId) !== undefined;
  const byContentHoveredTerm = useSelector(
    (state) => state.discovery.config.byContentHoveredTerm
  );
  const { hovered, handleMouseOver, handleMouseOut } = useDelayedHover({
    onMouseOver: () => {
      if (checked) {
        dispatch(setByContentHoveredTerm(item.NounPhraseEscaped));
      }
    },
    onMouseOut: () => {
      if (checked) {
        dispatch(setByContentHoveredTerm(null));
      }
    },
  });

  const handleChange = (e) => {
    if (e.target.checked) {
      dispatch(addChecked(item));
    } else {
      dispatch(setByContentHoveredTerm(null));
      dispatch(uncheckAndCollapse(item));
    }
  };

  const frequency = () => {
    if (documentView) {
      return (
        <Gradient content={item.Occurrences} maxOccurrences={maxOccurrences} />
      );
    }
    return <span>{item.Occurrences.toLocaleString()}</span>;
  };

  const bold = (itemToBeBolded) => {
    const regEx = new RegExp(itemToBeBolded.GroupName, "ig");
    const replaceMask = `<b>${itemToBeBolded.NounPhrase?.match(regEx)}</b>`;
    return itemToBeBolded.NounPhrase?.replace(regEx, replaceMask);
  };

  const itemCheckbox = () => (
    <Checkbox
      state={checked ? checkedTypes.CHECKED : checkedTypes.EMPTY}
      handleChange={handleChange}
      id={item}
      value={item}
      checkboxRef={checkboxRef}
    />
  );

  const getTerms = () => {
    let terms = checkedItems.map((term) => term.NounPhrase);

    if (!terms.includes(item.NounPhrase)) {
      terms.push(item.NounPhrase);
    }

    // remove duplicates
    terms = new Set(terms);
    terms = Array.from(terms);

    return terms;
  };

  return (
    <div
      ref={itemRef}
      className={`item${!hasOccurrences ? " greyed" : ""}`}
      data-testid="category-item"
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <div className="item__text">
        {itemCheckbox()}
        <Ellipsis
          text={parse(bold(item))}
          classes={clsx({
            checked,
            highlighted: compareStringsCaseInsensitive(
              item.NounPhrase,
              byContentHoveredTerm
            ),
          })}
        />
      </div>
      <div className="item__frequency">{frequency()}</div>
      {hovered && (
        <ActionButtons
          terms={getTerms()}
          term={item.NounPhrase}
          placement={PLACEMENTS.RIGHT}
          parentRef={itemRef}
          style={{ marginLeft: -5 }}
          alignBottom
        />
      )}
    </div>
  );
};

Item.propTypes = {
  item: PropTypes.shape(itemProps).isRequired,
  checked: PropTypes.bool.isRequired,
  maxOccurrences: PropTypes.number,
};

export default React.memo(Item);
